import { createSlice } from '@reduxjs/toolkit'
import {
  addIncident,
  calculateIncidentActionsCosts,
  deleteIncident,
  fetchIncidentsFromRelatedControls,
  getIncidentByActionId,
  getIncidentByMissionId,
  getIncidentbyId,
  getIncidents,
  mapIncidentToAudits,
  removeIncidentsFromAction,
  updateIncident,
} from './incidentActions'
import { Incident, IncidentResponse } from '../../types/incidentTypes'
import { Audit } from '../../types/auditTypes'

interface IncidentState {
  incidents: Incident[]
  relatedIncidents: Incident[]
  loading: boolean
  incident: Incident | null
  error: string | null
  addedIncident: Incident | null
  updatedIncident: Incident | null
  IncidentResponse: IncidentResponse | null
  sourceIncidents: Incident[]
  audits: Audit[]
  totalCost: number | null
}

const initialState: IncidentState = {
  incidents: [],
  relatedIncidents: [],
  loading: false,
  error: null,
  addedIncident: null,
  incident: null,
  updatedIncident: null,
  IncidentResponse: null,
  sourceIncidents: [],
  audits: [],
  totalCost: null,
}

export const incidentSlice = createSlice({
  name: 'audit',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getIncidents.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getIncidents.fulfilled, (state, action) => {
        state.loading = false
        state.IncidentResponse = {
          incidents: action.payload.incidents,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,

          status: action.payload.status,
          message: action.payload.message,
        }
      })
      .addCase(getIncidents.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(fetchIncidentsFromRelatedControls.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchIncidentsFromRelatedControls.fulfilled, (state, action) => {
        state.loading = false
        const incidents = Object.values(action.payload).filter(
          (item) => item && typeof item === 'object'
        )
        state.relatedIncidents = incidents
      })
      .addCase(fetchIncidentsFromRelatedControls.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Something went wrong'
      })
      .addCase(deleteIncident.fulfilled, (state, action) => {
        state.loading = false
        const deletedIncidentId = action.meta.arg

        if (state.IncidentResponse) {
          state.IncidentResponse.incidents = (
            state.IncidentResponse.incidents ?? []
          ).filter((incident) => incident._id !== deletedIncidentId)

          state.IncidentResponse.total = state.IncidentResponse.total ?? 0
          state.IncidentResponse.totalPages =
            state.IncidentResponse.totalPages ?? 0
          state.IncidentResponse.currentPage =
            state.IncidentResponse.currentPage ?? 0
        }

        if (state.incidents) {
          state.incidents = (state.incidents ?? []).filter(
            (incident) => incident._id !== deletedIncidentId
          )
        }
      })
      .addCase(addIncident.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addIncident.fulfilled, (state, action) => {
        state.loading = false
        state.addedIncident = action.payload
        state.incidents.push(action.payload)
      })
      .addCase(addIncident.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(updateIncident.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateIncident.fulfilled, (state, action) => {
        state.loading = false
        state.updatedIncident = action.payload
      })
      .addCase(updateIncident.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getIncidentbyId.pending, (state) => {
        state.loading = true
        state.error = null
      })

      .addCase(getIncidentbyId.fulfilled, (state, action) => {
        state.loading = false
        state.incident = action.payload
      })
      .addCase(getIncidentbyId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getIncidentByMissionId.pending, (state) => {
        state.loading = true
        state.error = null
      })

      .addCase(getIncidentByMissionId.fulfilled, (state, action) => {
        state.loading = false
        const missionIncidents = Object.values(action.payload).filter(
          (item) => item && typeof item === 'object'
        )
        state.incidents = missionIncidents as []
      })
      .addCase(getIncidentByMissionId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(getIncidentByActionId.pending, (state) => {
        state.loading = true
        state.error = null
      })

      .addCase(getIncidentByActionId.fulfilled, (state, action) => {
        state.loading = false
        const sourceIncidents = Object.values(action.payload).filter(
          (item) => item && typeof item === 'object'
        )
        state.sourceIncidents = sourceIncidents as []
      })
      .addCase(getIncidentByActionId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(removeIncidentsFromAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(removeIncidentsFromAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(removeIncidentsFromAction.fulfilled, (state, action) => {
        state.loading = false
        state.incidents = action.payload.incidents
      })
      .addCase(mapIncidentToAudits.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(mapIncidentToAudits.fulfilled, (state, action) => {
        state.loading = false
        state.audits = action.payload.audits as []
      })
      .addCase(mapIncidentToAudits.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to map incident to audits.'
      })
      .addCase(calculateIncidentActionsCosts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(calculateIncidentActionsCosts.fulfilled, (state, action) => {
        state.loading = false
        state.totalCost = action.payload.totalCost
      })
      .addCase(calculateIncidentActionsCosts.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload || 'Failed to calculate incident actions costs.'
      })
  },
})

export default incidentSlice.reducer
